import { isNumericalSplit } from '../leaderboard/utils/splits.ts'

export type Handedness = 'L' | 'R'
export type HandednessWithCombination = Handedness | 'X'

export type LeagueGroupContext = {
    pitchType: string | null
    pitchHand: HandednessWithCombination | null
    batSide: HandednessWithCombination | null
    year: number | null
}

export const LEAGUE_PREFIX = 'league-'

function isHandednessWithCombination(
    value: string
): value is HandednessWithCombination {
    return value === 'X' || value === 'R' || value === 'L'
}

export function asHandedness(value: unknown): HandednessWithCombination | null {
    if (typeof value === 'string' && isHandednessWithCombination(value)) {
        return value
    }

    return null
}

export function isLeagueKey(key: string): boolean {
    return key.startsWith(LEAGUE_PREFIX)
}

export function parseDataFromKey(key: string) {
    const [id, pitchType, pitchHand, batSide] = key.split('-')

    if (!isHandednessWithCombination(pitchHand)) {
        throw new Error('Invalid pitchHand')
    }
    if (!isHandednessWithCombination(batSide)) {
        throw new Error('Invalid batSide')
    }

    return {
        id,
        pitchType,
        pitchHand,
        batSide,
    }
}

export function parseUniqueId(
    idStr: string,
    splitProps: string[]
): Record<string, string | number> {
    const splitProperties = splitProps
        // This must match the server side
        .filter((d) => d !== 'api_game_date_month_text')
        .sort((a, b) => (a > b ? 1 : -1))

    const [id, ...splits] = idStr.split('_')

    if (splitProperties.length !== splits.length) {
        throw new Error('mismatch in splits')
    }

    const uniqueData: Record<string, string | number> = { id }
    splitProperties.forEach((prop, i) => {
        const val = splits[i]
        uniqueData[prop] = isNumericalSplit(prop) ? Number(val) : val
    })

    return uniqueData
}

export function getLeagueGroupKey(context: LeagueGroupContext): string {
    return [
        context.pitchType ?? 'all',
        context.pitchHand ?? 'all',
        context.batSide ?? 'all',
        context.year ?? 'all',
    ].join('|')
}

export function getLeagueCurveKey(context: LeagueGroupContext): string {
    return `${LEAGUE_PREFIX}${getLeagueGroupKey(context)}`
}
